import { Link } from "react-router-dom";
import { ShieldCheck, Scale, UserCheck, TrendingDown, ArrowRight, Check, MapPin, Clock } from "lucide-react";
import { BRAND } from "../data/brand";

export default function ChiSiamo() {
  return (
    <div data-testid="chi-siamo-page">
      {/* Hero */}
      <section className="relative bg-slate-50 border-b border-slate-200">
        <div className="container-90 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-end">
            <div className="lg:col-span-8">
              <div className="overline mb-6">Chi siamo</div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.02]">
                Broker assicurativi
                <br />
                con sede a Frosinone,
                <br />
                <span className="text-[color:var(--brand-blue)]">al servizio del cliente.</span>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-lg text-slate-600 leading-relaxed">
                90 Brokers Srls rende il mondo assicurativo più semplice, trasparente e
                vantaggioso per chi ci sceglie: famiglie, professionisti e imprese del
                territorio.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-16 mt-16 border-t border-slate-200">
            <div>
              <div className="font-heading text-4xl font-extrabold text-[color:var(--brand-blue)]">
                Frosinone
              </div>
              <div className="text-sm text-slate-500 mt-2">Sede operativa</div>
            </div>
            <div>
              <div className="font-heading text-4xl font-extrabold text-[color:var(--brand-blue)]">
                Indipendenti
              </div>
              <div className="text-sm text-slate-500 mt-2">Da ogni compagnia</div>
            </div>
            <div>
              <div className="font-heading text-4xl font-extrabold text-[color:var(--brand-blue)]">
                Certificati
              </div>
              <div className="text-sm text-slate-500 mt-2">Iscritti al RUI</div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section bg-white">
        <div className="container-90">
          <div className="max-w-3xl mb-16">
            <div className="overline mb-4">Valori</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
              Quattro principi non negoziabili.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: ShieldCheck,
                title: "Trasparenza",
                body: "Spieghiamo senza giri di parole che cosa copre davvero una polizza e quanto costa, dall'inizio alla fine.",
              },
              {
                icon: Scale,
                title: "Imparzialità",
                body: "Nessun vincolo con le compagnie: scegliamo solo in base al tuo interesse reale.",
              },
              {
                icon: UserCheck,
                title: "Personalizzazione",
                body: "Le nostre soluzioni nascono dall'ascolto: ogni cliente è una storia diversa.",
              },
              {
                icon: TrendingDown,
                title: "Convenienza",
                body: "Copertura migliore, costo inferiore: la convenienza per noi è un fatto concreto.",
              },
            ].map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={i}
                  data-testid={`value-${i}-card`}
                  className="bg-slate-50 p-8 border border-slate-200 hover:border-[color:var(--brand-blue)] hover:bg-white transition-all"
                >
                  <Icon className="w-8 h-8 text-[color:var(--brand-blue)] mb-6" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">{v.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* La nostra sede */}
      <section data-testid="chisiamo-sede-section" className="bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[460px]">
          <div className="lg:col-span-5 bg-slate-50 border-y border-slate-200 flex items-center">
            <div className="px-6 lg:pl-[calc((100vw-1280px)/2+24px)] lg:pr-16 py-16 lg:py-24 w-full">
              <div className="overline mb-5">La nostra sede</div>
              <h2 className="font-heading text-3xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.05] mb-8">
                Ti aspettiamo
                <br />
                <span className="text-[color:var(--brand-blue)]">a Frosinone.</span>
              </h2>
              <p className="text-slate-600 leading-relaxed mb-10 max-w-md">
                Il nostro ufficio si trova nel cuore della città. Passa a trovarci per una
                consulenza di persona, oppure scrivici per fissare un appuntamento dedicato.
              </p>

              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[color:var(--brand-blue)] flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-white" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">
                      Indirizzo
                    </div>
                    <div className="font-heading text-base font-bold text-slate-900 leading-tight">
                      {BRAND.address.street}
                      <br />
                      03100 {BRAND.address.city} (FR)
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[color:var(--brand-blue)] flex items-center justify-center">
                    <Clock className="w-4 h-4 text-white" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">
                      Orari di apertura
                    </div>
                    <div className="font-heading text-base font-bold text-slate-900 leading-snug">
                      {BRAND.hours.weekdays}
                      <br />
                      {BRAND.hours.saturday}
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7 min-h-[360px] lg:min-h-full">
            <iframe
              title="90 Brokers Srls — Mappa sede"
              data-testid="chisiamo-map-iframe"
              src="https://www.google.com/maps?q=Via+Piave+67,+Frosinone&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, display: "block", minHeight: "460px" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* Mission stripe */}
      <section className="bg-[#0a0f1f] text-white py-24">
        <div className="container-90 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline text-white/60 mb-6">Missione</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] mb-8">
              Rendere l'assicurazione una scelta consapevole, non un obbligo.
            </h2>
            <p className="text-slate-300 leading-relaxed text-lg max-w-2xl">
              Vogliamo che ogni cliente comprenda esattamente cosa sta firmando, perché lo sta
              firmando e come quella polizza protegge davvero la sua vita, la sua famiglia o la
              sua impresa.
            </p>
          </div>
          <div className="lg:col-span-5">
            <ul className="space-y-4">
              {[
                "Linguaggio chiaro, zero tecnicismi inutili",
                "Preventivi multipli sempre confrontabili",
                "Accompagnamento completo in fase di sinistro",
                "Revisione periodica delle coperture",
              ].map((p, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <Check className="w-5 h-5 text-[color:var(--brand-blue-light)] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">{p}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/contatti"
              data-testid="chisiamo-cta-link"
              className="mt-10 inline-flex items-center gap-2 btn-primary"
            >
              Fissa una consulenza <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
